import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Button, Size, Type } from '@platform/witems';
import { useProgramPeriods } from '../state/data/carePeriod';
import { useExceptionTasks } from '../state/data/task';
import { CARE_TEAM_JOURNEY, CARE_TEAM_LABELS, collectCareTeamExceptions } from '../logic/careTeamJourney';
import { resolveCareTeamScope, careTeamTaskInScope, careTeamSnoozed } from '../logic/careTeamScope';

export default function CareTeamExceptions() {
  const { programid = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const [shown, setShown] = useState(50);
  const [clock, setClock] = useState(Date.now());
  const periods = useProgramPeriods(programid);
  const scope = useMemo(() => resolveCareTeamScope(periods.data || [], programid, clock), [periods.data, programid, clock]);
  const ids = useMemo(() => periods.data && !periods.isError ? scope.periods.map(cp => String(cp.care_period_id)).sort() : undefined, [periods.data, periods.isError, scope]);
  const tasks = useExceptionTasks(programid, ids);
  const result = useMemo(() => collectCareTeamExceptions((tasks.data || []).filter(t => careTeamTaskInScope(t, scope.periods)), {
    now: clock, labels: CARE_TEAM_LABELS, snoozed: t => careTeamSnoozed(t, clock),
  }), [tasks.data, scope, clock]);
  const group = params.get('group') || '';
  const role = params.get('role') || '';
  const urgent = params.get('urgent') === '1';
  const items = result.items.filter(x => (!group || x.group === group) && (!role || x.role === role) && (!urgent || x.reason === 'urgent'));
  const failed = periods.isError || tasks.isError;
  const loading = periods.isPending || tasks.isPending;
  const change = (key, value) => {
    setShown(50);
    setParams(prev => { const next = new URLSearchParams(prev); if (value) next.set(key, value); else next.delete(key); return next; });
  };
  const refresh = async () => { setClock(Date.now()); await periods.refetch(); await tasks.refetch(); };
  const reason = x => x.reason === 'urgent' ? 'Urgent' : x.reason === 'overdue' ? (x.age ? `${x.age}d past due` : 'Past due today') : x.reason === 'due_today' ? 'Due today' : x.reason === 'verify_visit' ? 'Visit time passed — verify check-out' : x.reason === 'verify_availability' ? 'Verify availability / deadline' : 'Clinical review open';
  return (
    <section style={{ padding: 24 }}>
      <h1>Care-team task exceptions</h1>
      <p>Current task records, using the digest’s milestone rules. Not a frozen copy of yesterday’s message. Other open work is not completed.</p>
      <Link to={`/team/${encodeURIComponent(programid)}`}>Back to dashboard</Link>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '20px 0' }}>
        <label>Milestone{' '}
          <select aria-label="Milestone" value={group} onChange={e => change('group', e.target.value)}>
            <option value="">All milestones</option><option value="clinical">Clinical</option>
            {CARE_TEAM_JOURNEY.map(([id, title]) => <option key={id} value={id}>{title}</option>)}
          </select>
        </label>
        <label>Responsible role{' '}
          <select aria-label="Responsible role" value={role} onChange={e => change('role', e.target.value)}>
            <option value="">All roles</option><option>CC</option><option>Clinician</option><option>Role unassigned</option>
          </select>
        </label>
        <label><input type="checkbox" checked={urgent} onChange={e => change('urgent', e.target.checked ? '1' : '')} /> Urgent only</label>
        <Button size={Size.MINI} type={Type.PRIMARY} onClick={refresh}>Refresh exceptions</Button>
      </div>
      {failed ? <p role="alert">Exception review incomplete. Could not load all tasks or care episodes. This is not an all-clear; refresh to retry.</p>
        : loading ? <p role="status">Loading all eligible task pages…</p> : (
          <>
            <p>{items.length} matching task exceptions · {result.totals.open} open tasks checked · {result.totals.snoozed} snoozed</p>
            {!!scope.ambiguous.length && <p role="alert">{scope.ambiguous.length} ambiguous care episodes excluded pending verification.</p>}
            <p>Checked at {new Date(clock).toLocaleString()}. Refresh after completing work.</p>
            {items.length === 0 ? <p>No matching task exceptions identified. Other open or unverified work may remain.</p> : (
              <table className="table-component" style={{ width: '100%' }}>
                <thead><tr><th>Patient</th><th>Milestone</th><th>Role</th><th>Next action</th><th>Why now</th></tr></thead>
                <tbody>{items.slice(0, shown).map(x => (
                  <tr key={x.key}>
                    <td><Link to={`/team/${encodeURIComponent(programid)}/patient/${encodeURIComponent(x.uid)}`}>Patient {x.uid}</Link></td>
                    <td>{x.group}</td><td>{x.role}</td><td>{x.label}</td><td>{reason(x)}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            {shown < items.length && <Button size={Size.MINI} type={Type.PRIMARY} onClick={() => setShown(n => n + 50)}>Show more exceptions ({items.length - shown} remaining)</Button>}
          </>
        )}
    </section>
  );
}
